import { createFileRoute, Link } from '@tanstack/react-router';

import { SettingsLoadingState } from '@apps/web/components/settings/settings_page_shell';
import { TeamNotificationBulkEditor } from '@apps/web/components/settings/team_notification_bulk_editor';
import {
    SettingsPageShell,
    useSettingsGates,
} from '@apps/web/features/settings';
import {
    isNotificationChannelSlug,
    type NotificationChannelSlug,
} from '@apps/web/lib/notification_channel_pages';
import { isOrganizationAdmin } from '@apps/web/lib/organization_permissions';
import { useUserProfile } from '@apps/web/swr_data_fetching/current_user_queries';

export const Route = createFileRoute('/_app/settings/team-members_/bulk')({
    validateSearch: (
        search: Record<string, unknown>,
    ): { channel?: NotificationChannelSlug } => ({
        channel:
            typeof search['channel'] === 'string' &&
            isNotificationChannelSlug(search['channel'])
                ? search['channel']
                : undefined,
    }),
    component: TeamNotificationBulkPage,
});

function TeamNotificationBulkPage() {
    const { channel } = Route.useSearch();
    const { organizationId, isLoading } = useSettingsGates();
    const { data: profile, isLoading: isLoadingProfile } = useUserProfile({
        reason: 'bulk-notification-access',
    });
    if (isLoading || isLoadingProfile) {
        return <SettingsLoadingState />;
    }
    const canManage = isOrganizationAdmin(profile, organizationId);
    return (
        <SettingsPageShell
            title="Update team notifications"
            description="Choose teammates, edit their notification preferences, and review exactly what will change before saving."
            eyebrow={
                <Link
                    to="/settings/team-members"
                    className="text-muted-foreground hover:text-foreground text-sm pointer-coarse:inline-flex pointer-coarse:min-h-11 pointer-coarse:items-center"
                >
                    ← Team Members
                </Link>
            }
        >
            {canManage && organizationId ? (
                <TeamNotificationBulkEditor
                    key={organizationId}
                    organizationId={organizationId}
                    channel={channel}
                />
            ) : (
                <p role="alert">
                    Only organization admins can manage team notifications.
                </p>
            )}
        </SettingsPageShell>
    );
}
